import React, { useId } from 'react';

import type { RadioProps } from '../../types/componentType';

const sizeTokens = {
  small: {
    wrapper: 'w-9 h-9',
    wrapperPadding: 'bsds-p-4',
    button: 'w-5.5 h-5.5',
    dot: 'w-2 h-2'
  },
  large: {
    wrapper: 'w-11 h-11',
    wrapperPadding: 'bsds-p-6',
    button: 'w-8 h-8',
    dot: 'w-3 h-3'
  }
} as const;

const colorTokens = {
  primary: {
    border: 'border-primary-600',
    dot: 'bg-primary-600',
    background: 'bg-primary-50',
    focusRing: 'focus:ring-primary-200'
  },
  secondary: {
    border: 'border-secondary-600',
    dot: 'bg-secondary-600',
    background: 'bg-secondary-50',
    focusRing: 'focus:ring-secondary-200'
  }
} as const;

const Radio: React.FC<RadioProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'small',
  color = 'primary',
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const generatedId = useId();
  const radioId = id || generatedId;
  const { wrapper, wrapperPadding, button, dot } = sizeTokens[size];
  const { border, dot: dotColor, background, focusRing } = colorTokens[color];

  const baseContainer =
    `rounded-full border-1 border-gray-900 transition-colors duration-200 flex items-center justify-center bg-white ${button}`;

  const stateClasses = disabled
    ? 'cursor-click'
    : `cursor-pointer hover:border-opacity-80 ${focusRing} focus:ring-2 focus:ring-offset-2`;

  const checkedClasses = checked
    ? `${border} ${background}`
    : 'border-gray-300 bg-white';

  const handleToggle = React.useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [checked, disabled, onChange]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <div
      className={`bsds-display-flex bsds-justify-content-center bsds-align-items-center rounded-full ${wrapper} ${wrapperPadding} ${checked ? (color === 'primary' ? 'bg-primary-100' : 'bg-secondary-100') : ''
        }`}
    >
      <button
        type="button"
        id={radioId}
        data-testid={dataTestId}
        role="radio"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={ariaLabel || (checked ? 'Selecionado' : 'Não selecionado')}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        className={`inline-flex items-center justify-center focus:outline-none bg-transparent border-0 p-0 ${button}`}
      >
        <span
          className={`${baseContainer} ${stateClasses} ${checkedClasses}`}
          aria-hidden="true"
        >
          <span
            className={`rounded-full transform transition-all duration-200 ${dot} ${checked ? `${dotColor} scale-100` : 'bg-transparent scale-0'}`}
            aria-hidden="true"
          />
        </span>
      </button>
    </div>
  );
};

export default Radio;


